import { useAuthenticator } from '@aws-amplify/ui-react';
import { Button, Spinner } from '@nrg/design-system';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { cognitoErrorAuthKey } from '@/auth/cognito-error-message';
import AuthFooterLink from '@/auth/components/AuthFooterLink';
import AuthScreen from '@/auth/components/AuthScreen';
import FormField from '@/auth/components/FormField';

const schema = z.object({ username: z.string().trim().email() });

export default function ForgotPasswordScreen() {
  const { t } = useTranslation('auth');
  const { isPending, error, submitForm, toSignIn } = useAuthenticator((c) => [
    c.isPending,
    c.error,
    c.route,
  ]);

  const [username, setUsername] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const parsed = schema.safeParse({ username });
  const usernameError =
    !parsed.success && (touched || submitAttempted) ? t('errors.email_invalid') : undefined;

  const cognitoKey = cognitoErrorAuthKey(error);
  const banner = cognitoKey ? t(cognitoKey) : null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (!parsed.success) return;
    submitForm({ username: parsed.data.username });
  };

  return (
    <AuthScreen
      heading={t('forgot.heading')}
      subhead={t('forgot.subhead')}
      error={banner}
      onSubmit={handleSubmit}
      footer={
        <AuthFooterLink onClick={() => toSignIn()}>{t('common.back_to_sign_in')}</AuthFooterLink>
      }
    >
      <FormField
        name="username"
        label={t('forgot.email')}
        type="email"
        autoComplete="username"
        autoFocus
        spellCheck={false}
        autoCapitalize="off"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        onBlur={() => setTouched(true)}
        error={usernameError}
        disabled={isPending}
      />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={isPending}
        iconLeft={isPending ? <Spinner size="sm" /> : undefined}
      >
        {t('forgot.submit')}
      </Button>
    </AuthScreen>
  );
}
